import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	SecurityFeatureType,
	SecurityRankDefinitionRes,
} from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { SecurityFeature } from '@dealstack/shared/lib/common/securities/securityFeature';
import {
	BlockReason,
	FieldLockCondition,
	StateCondition,
} from '@dealstack/shared/lib/common/securities/securityFeatureRules';

export interface SecurityFeatureBaseProps {
	refreshDetails?: (securityId: string) => void;
	onCancel?: () => void;
	security: DataSecurityWithAllFeaturesAndRanksTct | null;
	hasEditorPermission?: boolean;
	relativeRanksMap?: Map<string, RelativeRank>;
}

export const makeBooleanDropdownOptions = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>
) => [
	{
		label: tCommonUi('boolean.true'),
		value: 'true',
	},
	{
		label: tCommonUi('boolean.false'),
		value: 'false',
	},
];

export interface RelativeRank extends SecurityRankDefinitionRes {
	relativeSortOrder: number;
}

const missingStateMessage = (condition: StateCondition): string => {
	switch (condition) {
		case 'ISSUED_IN_UNITS':
			return 'The security must be issued in units.';
		case 'RESIDUE_PARTICIPATION':
			return 'The security must participate in the residue.';
		case 'NO_RESIDUE_PARTICIPATION':
			return 'The security must not participate in the residue.';
		case 'NOT_EXCHANGE_TARGET':
			return 'A security targeted by another exchange right cannot use this feature.';
	}
};

const fieldLockMessage = (condition: FieldLockCondition): string => {
	switch (condition) {
		case 'IS_EXCHANGE_TARGET':
			return 'This field is locked because another security exchanges into it.';
		case 'NOT_ISSUED_IN_UNITS':
			return 'This field is only available when the security is issued in units.';
		case 'NO_RESIDUE_PARTICIPATION':
			return 'This field requires residue participation.';
		case 'ISSUER_LACKS_NOMINEE_STRUCTURE':
			return 'The issuer does not have a nominee structure.';
	}
};

export const securityFeatureBlockMessage = (reason: BlockReason): string => {
	switch (reason.type) {
		case 'MISSING_STATE':
			return missingStateMessage(reason.condition);
		case 'MISSING_FEATURE':
			return `${reason.feature.replaceAll('_', ' ').toLowerCase()} is required.`;
		case 'EXCLUDED_FEATURE':
		case 'CONTRADICTORY_STATE':
			return `This feature is incompatible with ${reason.feature
				.replaceAll('_', ' ')
				.toLowerCase()}.`;
		case 'DEFERRED_PREDICATE':
			return 'This compatibility check will be completed when the feature is saved.';
		case 'FIELD_LOCK':
			return fieldLockMessage(reason.condition);
	}
};

export const securityFeatureBlockTranslation = (
	reason: BlockReason
): { key: string; defaultValue: string } => {
	const defaultValue = securityFeatureBlockMessage(reason);

	switch (reason.type) {
		case 'MISSING_STATE':
			return {
				key: `securities.feature_rules.missing_state.${reason.condition}`,
				defaultValue,
			};
		case 'MISSING_FEATURE':
			return {
				key: `securities.feature_rules.missing_feature.${reason.feature}`,
				defaultValue,
			};
		case 'EXCLUDED_FEATURE':
			return {
				key: `securities.feature_rules.excluded_feature.${reason.feature}`,
				defaultValue,
			};
		case 'CONTRADICTORY_STATE':
			return {
				key: `securities.feature_rules.contradictory_state.${reason.condition}.${reason.feature}`,
				defaultValue,
			};
		case 'DEFERRED_PREDICATE':
			return {
				key: `securities.feature_rules.deferred.${reason.predicate}`,
				defaultValue,
			};
		case 'FIELD_LOCK':
			return {
				key: `securities.feature_rules.field_lock.${reason.condition}`,
				defaultValue,
			};
	}
};

const translateSecurityFeature = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	feature: SecurityFeature
): string => {
	switch (feature) {
		case SecurityFeature.AUTHORISED_CAPITAL:
			return tCapManage('securities.authorisedCapital.title');
		case SecurityFeature.INCOME_RIGHTS_INTEREST:
			return tCapManage('securities.incomeRights_interest.title');
		case SecurityFeature.INCOME_RIGHTS_TRACKING:
			return tCapManage('securities.incomeRights_tracking.title');
		case SecurityFeature.REDEMPTION_RIGHTS:
			return tCapManage('securities.redemptionRights.title');
		case SecurityFeature.CATCH_UP_RIGHTS:
			return tCapManage('securities.catch_up_rights.title');
		case SecurityFeature.EXCHANGE_RIGHTS:
			return tCapManage('securities.exchange_rights.title');
		case SecurityFeature.LIQUIDATION_PREFERENCE:
			return tCapManage('securities.liquidation_preference.title');
		case SecurityFeature.PARTICIPATION_THRESHOLD:
			return tCapManage('securities.participationThreshold.title');
		case SecurityFeature.RATCHETING:
			return tCapManage('securities.ratcheting.title');
	}
};

const translateClearedState = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	condition: StateCondition
): string => {
	switch (condition) {
		case 'RESIDUE_PARTICIPATION':
			return tCapManage('security_details.feature_rules.state.residue_rights_cleared', {
				defaultValue: 'residue rights will be turned off',
			});
		case 'ISSUED_IN_UNITS':
			return tCapManage('security_details.feature_rules.state.issued_in_units_cleared', {
				defaultValue: 'issued-in-units state will be cleared',
			});
		case 'NO_RESIDUE_PARTICIPATION':
			return tCapManage('security_details.feature_rules.state.no_residue_cleared', {
				defaultValue: 'no-residue state will be cleared',
			});
		case 'NOT_EXCHANGE_TARGET':
			return tCapManage('security_details.feature_rules.state.exchange_target_cleared', {
				defaultValue: 'exchange-target state will be cleared',
			});
	}
};

export const makeCascadeWarningDescription = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	features: readonly SecurityFeature[],
	clearedStates: readonly StateCondition[]
): string => {
	const changes = [
		...features.map((feature) => translateSecurityFeature(tCapManage, feature)),
		...clearedStates.map((condition) => translateClearedState(tCapManage, condition)),
	].join(', ');

	return tCapManage('security_details.feature_rules.warning_modal.description', {
		defaultValue: 'Saving this change will remove or reset: {{changes}}.',
		changes,
	});
};

// Uniform prefix + bolded emphasis + suffix so every bullet in the cascade popup bolds the
// "thing being changed" (feature name, state name) and reads naturally as imperative.
export interface FeatureAdditionChange {
	prefix: string;
	emphasis: string;
	suffix?: string;
}

const stateChangeItem = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	condition: StateCondition
): FeatureAdditionChange => {
	const residueRightsLabel = tCapManage(
		'security_details.feature_rules.feature_addition_cascade.residue_rights_noun',
		{ defaultValue: 'Rights to residue' }
	);
	switch (condition) {
		case 'RESIDUE_PARTICIPATION':
			return {
				prefix: 'Disable ',
				emphasis: residueRightsLabel,
			};
		case 'ISSUED_IN_UNITS':
			return {
				prefix: 'Clear the ',
				emphasis: tCapManage(
					'security_details.feature_rules.feature_addition_cascade.issued_in_units_noun',
					{ defaultValue: 'issued-in-units' }
				),
				suffix: ' state',
			};
		case 'NO_RESIDUE_PARTICIPATION':
			return {
				prefix: 'Clear the ',
				emphasis: tCapManage(
					'security_details.feature_rules.feature_addition_cascade.no_residue_noun',
					{ defaultValue: 'no-residue' }
				),
				suffix: ' state',
			};
		case 'NOT_EXCHANGE_TARGET':
			return {
				prefix: 'Clear the ',
				emphasis: tCapManage(
					'security_details.feature_rules.feature_addition_cascade.exchange_target_noun',
					{ defaultValue: 'exchange-target' }
				),
				suffix: ' state',
			};
	}
};

export interface FeatureAdditionCascadeContent {
	intro: string;
	changes: FeatureAdditionChange[];
}

export interface FeatureAdditionBlockedContent {
	intro: string;
	reasons: string[];
}

export const makeFeatureAdditionCascadeContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	feature: SecurityFeature,
	cascade: { clearedStates: readonly StateCondition[]; features: readonly SecurityFeature[] },
	residueWouldFlipOn: boolean,
	residueWouldFlipOff: boolean
): FeatureAdditionCascadeContent => {
	const residueRightsLabel = tCapManage(
		'security_details.feature_rules.feature_addition_cascade.residue_rights_noun',
		{ defaultValue: 'Rights to residue' }
	);
	const changes: FeatureAdditionChange[] = [];

	if (residueWouldFlipOn) {
		changes.push({ prefix: 'Enable ', emphasis: residueRightsLabel });
	}
	if (residueWouldFlipOff) {
		changes.push({ prefix: 'Disable ', emphasis: residueRightsLabel });
	}
	for (const condition of cascade.clearedStates) {
		changes.push(stateChangeItem(tCapManage, condition));
	}
	for (const removed of cascade.features) {
		changes.push({
			prefix: 'Remove ',
			emphasis: translateSecurityFeature(tCapManage, removed),
		});
	}

	const intro = tCapManage('security_details.feature_rules.feature_addition_cascade.intro', {
		defaultValue: 'Adding {{feature}} will also:',
		feature: translateSecurityFeature(tCapManage, feature),
	});

	return { intro, changes };
};

export const makeFeatureAdditionBlockedContent = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	feature: SecurityFeature,
	hardBlockers: readonly BlockReason[]
): FeatureAdditionBlockedContent => {
	const reasons = hardBlockers.map((reason) => {
		const { key, defaultValue } = securityFeatureBlockTranslation(reason);
		return tCapManage(key, { defaultValue });
	});

	const intro = tCapManage('security_details.feature_rules.feature_addition_blocked.intro', {
		defaultValue: "{{feature}} can't be added:",
		feature: translateSecurityFeature(tCapManage, feature),
	});

	return { intro, reasons };
};

// Deep-links to the Manage seniority page focused on a specific security + rank dimension.
// The ranking page reads these params to scroll to and highlight the matching card.
export const buildSecurityRankingDeepLink = (
	securityId: string | undefined,
	featureType: SecurityFeatureType
) => {
	const params = new URLSearchParams({ featureType });
	if (securityId) {
		params.set('securityId', securityId);
	}

	return `..${PEOPLE_ROUTES.SECURITY_RANKING}?${params.toString()}`;
};
